import { Button } from '@consta/uikit/Button'
import { ContextMenu } from '@consta/uikit/ContextMenu'
import type { MouseEvent, ReactNode } from 'react'
import { useRef, useState } from 'react'

import { FormattedTooltipContent } from '../../FormattedTooltipContent'
import { IconCorner } from '../../Icons'
import { TooltipTarget } from '../../TooltipTarget'
import type { ContainerChild, ToolContainer, ToolVariant, ToolVariantProps } from '../types'
import css from './Cards.css'
import { isToolActive } from './helpers'

const renderLeftSide = <T extends string>(tool: ToolVariant<T>) => {
  const Icon = tool.icon

  if (!Icon) return

  return <Icon />
}

type CardToolContainerProps<T extends string> = ToolVariantProps<T, ToolContainer<T>>

export function CardToolContainer<T extends string>({
  tool,
  activeTools,
  onToolChanged,
}: CardToolContainerProps<T>) {
  type ConcreteTool = ToolVariant<T>

  const anchorRef = useRef<HTMLButtonElement | null>(null)

  const [toolToShow, setToolToShow] = useState<ConcreteTool>(tool.getDefaultTool(tool.children))
  const [showChildren, setShowChildren] = useState<boolean>(false)

  const isActiveTool = isToolActive(activeTools, toolToShow)

  const hideChildren = () => {
    setShowChildren(false)
  }

  const toggleChildren = () => {
    setShowChildren(prev => !prev)
  }

  const handleChoose = (tool: ContainerChild<T>) => {
    if (showChildren) {
      setToolToShow(tool)
      hideChildren()
    }

    onToolChanged(tool)
  }

  const handleContextMenu = (e: MouseEvent) => {
    e.preventDefault()
    toggleChildren()
  }

  const content = (
    <FormattedTooltipContent title={tool.title} description={tool.description} />
  ) as ReactNode & string

  return (
    <>
      <TooltipTarget className={css.button} tooltipProps={{ content }}>
        <Button
          ref={anchorRef}
          data-testid={tool.toolId}
          size="s"
          view={isActiveTool ? 'primary' : 'clear'}
          onlyIcon={true}
          iconLeft={toolToShow.icon}
          disabled={tool.disabled}
          onClick={toggleChildren}
          onContextMenu={handleContextMenu}
        />
        <IconCorner className={css.icon} size="xs" onClick={toggleChildren} />
      </TooltipTarget>
      <ContextMenu
        isOpen={showChildren}
        anchorRef={anchorRef}
        direction="rightDown"
        size="s"
        items={tool.children}
        getItemLeftSide={renderLeftSide}
        getItemLabel={(tool: ConcreteTool) => tool.title}
        getItemDisabled={item => item.disabled}
        onItemClick={handleChoose}
        onClickOutside={hideChildren}
      />
    </>
  )
}
